import React, { useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Container,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert
} from '@mui/material';

const ContractAccepted: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const sessionId = searchParams.get('session_id');
  const message = location.state?.message;
  const acceptedAt = location.state?.acceptedAt;

  useEffect(() => {
    const processPayment = async () => {
      if (sessionId) {
        try {
          // Get contract token from localStorage
          const contractToken = localStorage.getItem('contractToken');
          if (!contractToken) {
            setError('Contract token not found. Please try again or contact support.');
            setLoading(false);
            return;
          }

          // Accept contract with session ID and token
          await axios.post(`/api/contract/public/accept/${contractToken}`, {
            stripeSessionId: sessionId
          });

          // Clear contract token from storage
          localStorage.removeItem('contractToken');
          
          setLoading(false);
        } catch (err: any) {
          console.error('Error processing payment confirmation:', err);
          setError(err.response?.data?.message || 'Failed to process payment confirmation');
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    processPayment();
  }, [sessionId]);

  if (loading) {
    return (
      <Container maxWidth="md">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box my={4}>
        <Paper elevation={3} sx={{ p: 4 }}>
          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : (
            <>
              <Typography variant="h4" component="h1" gutterBottom>
                Contract Accepted
              </Typography>
              <Typography variant="body1" paragraph>
                {message || 'Thank you for accepting the contract and completing your payment. Your counselling journey can now begin.'}
              </Typography>
              {acceptedAt && (
                <Typography variant="body2" color="textSecondary">
                  Accepted on: {new Date(acceptedAt).toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </Typography>
              )}
              <Typography variant="body1" paragraph sx={{ mt: 2 }}>
                Our team will be in touch shortly to assign you a counsellor and arrange your first session.
              </Typography>
              <Typography variant="body1">
                If you have any questions in the meantime, please don't hesitate to contact us.
              </Typography>
            </>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default ContractAccepted;
